/**
 * Valida el RPC de una red personalizada y detecta su chainId real (nunca se
 * confía en uno escrito a mano). Se valida directo desde el navegador, no vía
 * nuestro backend — proxear una URL arbitraria del usuario sería abrir la
 * puerta a SSRF.
 */

export interface NetworkValidationResult {
  isValid: boolean;
  detectedChainId?: number;
  error?: 'unreachable' | 'invalid_response';
}

const networkValidationService = {
  async validateRpc(rpcUrl: string): Promise<NetworkValidationResult> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_chainId', params: [] }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return { isValid: false, error: 'unreachable' };
      }

      const data = await response.json();
      const hexChainId = data?.result;

      if (!hexChainId || typeof hexChainId !== 'string' || !hexChainId.startsWith('0x')) {
        return { isValid: false, error: 'invalid_response' };
      }

      const detectedChainId = parseInt(hexChainId, 16);
      if (!Number.isFinite(detectedChainId) || detectedChainId <= 0) {
        return { isValid: false, error: 'invalid_response' };
      }

      return { isValid: true, detectedChainId };
    } catch {
      return { isValid: false, error: 'unreachable' };
    }
  },
};

export default networkValidationService;
